import { create } from 'zustand';
import { getOrganizations, getOrganization, createOrganization, updateOrganization, deleteOrganization } from '../services/organization.service';
import { OrganizationSchema, OrganizationRequest, UpdateOrganizationRequest, OrganizationPagination, OrganizationQueryParams } from '../types/organization.type';

interface OrganizationState {
  organizations: OrganizationSchema[];
  total: number;
  page: number;
  limit: number;
  selectedOrganization: OrganizationSchema | null;
  isLoading: boolean;
  error: string | null;
  showOrganizationModal: boolean;
  setSelectedOrganization: (organization: OrganizationSchema | null) => void;
  setShowOrganizationModal: (show: boolean) => void;
  getOrganizations: (params?: OrganizationQueryParams) => Promise<OrganizationPagination | null>;
  fetchOrganizationById: (organizationId: string) => Promise<OrganizationSchema | null>;
  createOrganization: (organization: OrganizationRequest) => Promise<OrganizationSchema | null>;
  updateOrganization: (organizationId: string, organization: UpdateOrganizationRequest) => Promise<OrganizationSchema | null>;
  deleteOrganization: (organizationId: string) => Promise<boolean>;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

function getInitialSelectedOrganization(): OrganizationSchema | null {
  try {
    const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('selectedOrganization') : null;
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

export const useOrganizationStore = create<OrganizationState>((set) => ({
  organizations: [],
  total: 0,
  page: 1,
  limit: 10,
  selectedOrganization: getInitialSelectedOrganization(),
  isLoading: false,
  error: null,
  showOrganizationModal: false,
  setShowOrganizationModal: (show: boolean) => set({ showOrganizationModal: show }),
  setSelectedOrganization: (organization) => set({ selectedOrganization: organization }),
  getOrganizations: async (params?: OrganizationQueryParams) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getOrganizations(params);
      const data = response as GenericResponse<OrganizationPagination>;
      set({
        organizations: data.result.organizations,
        total: data.result.total,
        page: data.result.page,
        limit: data.result.limit,
        isLoading: false
      });
      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to get organizations', isLoading: false });
      return null;
    }
  },

  fetchOrganizationById: async (organizationId) => {
    set({ error: null });
    try {
      const response = await getOrganization(organizationId);
      const data = response as GenericResponse<OrganizationSchema>;
      set((state) => ({
        organizations: state.organizations.some((o) => o.id === organizationId)
          ? state.organizations.map((org) =>
              org.id === organizationId ? data.result : org
            )
          : [...state.organizations, data.result],
      }));
      return data.result;
    } catch (error: any) {
      set({
        error: error.response?.data?.["detail"] || "Failed to load organization",
      });
      return null;
    }
  },

  createOrganization: async (organization) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createOrganization(organization);
      const data = response as GenericResponse<OrganizationSchema>;
      
      set((state) => ({ 
        organizations: [...state.organizations, data.result],
        isLoading: false 
      }));

      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to create organization', isLoading: false });
      return null;
    }
  },

  updateOrganization: async (organizationId, organization) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateOrganization(organizationId, organization);
      const data = response as GenericResponse<OrganizationSchema>;
      
      set((state) => ({ 
        organizations: state.organizations.map(org => 
          org.id === organizationId ? data.result : org
        ),
        isLoading: false 
      }));

      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to update organization', isLoading: false });
      return null;
    }
  },

  deleteOrganization: async (organizationId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteOrganization(organizationId);
      
      set((state) => ({ 
        organizations: state.organizations.filter(org => org.id !== organizationId),
        isLoading: false 
      }));

      return true;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to delete organization', isLoading: false });
      return false;
    }
  },
}));